import { Injectable } from '@angular/core';
import { VoiceRecorder } from 'capacitor-voice-recorder';
import { Directory, Filesystem } from '@capacitor/filesystem';

type RecState = 'idle' | 'rec' | 'stopping';

@Injectable({ providedIn: 'root' })
export class VoiceService {
    state: RecState = 'idle';
    private startedAt = 0;
    private timer?: any;

    private async ensurePerms() {
        const { value: has } = await VoiceRecorder.hasAudioRecordingPermission();
        if (!has) {
            const { value } = await VoiceRecorder.requestAudioRecordingPermission();
            if (!value) throw new Error('Microphone permission denied');
        }
        const { value: can } = await VoiceRecorder.canDeviceVoiceRecord();
        if (!can) throw new Error('Device/browser cannot record');
    }

    async start(maxMs = 120_000, useFile = true) {
        if (this.state !== 'idle') return;
        await this.ensurePerms();
        // Pass a directory to save to file instead of returning base64 (recommended)
        const opts = useFile ? { directory: Directory.Data, subDirectory: 'voices' } : undefined;
        await VoiceRecorder.startRecording(opts as any);
        this.state = 'rec';
        this.startedAt = Date.now();
        this.timer = setTimeout(() => this.stop().catch(() => { }), maxMs);
    }

    async stop(): Promise<{ blob: Blob; durationMs: number; mime: string }> {
        if (this.state !== 'rec') throw new Error('Not recording');
        this.state = 'stopping';
        clearTimeout(this.timer);

        const res = await VoiceRecorder.stopRecording();
        // Safety: some typings put payload under res.value
        const v = (res as any).value ?? res;

        const durationMs: number = v.msDuration ?? (Date.now() - this.startedAt);
        const mime: string = v.mimeType || 'audio/aac';

        let payload: string | Blob | undefined;

        if (v.path) {
            // Read the saved file. Depending on platform, Filesystem may return base64 or a data URL.
            const read = await Filesystem.readFile({ directory: Directory.Data, path: v.path as string });
            // read.data is a string (either base64 or "data:...").
            payload = read.data as string;
        } else if (v.recordDataBase64) {
            payload = v.recordDataBase64 as string;     // base64 string
        } else if (v.blob) {
            payload = v.blob as Blob;                   // some web impls may return a Blob
        } else {
            throw new Error('Recorder returned no data (neither path, base64, nor blob)');
        }

        const { blob } = await this.normalizeToBlob(payload, mime);

        this.state = 'idle';
        return { blob, durationMs, mime };
    }

    async delete() {
        if (this.state !== 'rec') throw new Error('Not recording');
        clearTimeout(this.timer);
        await VoiceRecorder.stopRecording();
        this.state = 'idle';
    }


    async base64ToBlob(b64: string, mime = 'application/octet-stream'): Promise<Blob> {
        const res = await fetch(`data:${mime};base64,${b64}`);
        return await res.blob();
    }
    async normalizeToBlob(
        input: string | Blob,
        mime = 'application/octet-stream'
    ): Promise<{ blob: Blob; mime: string }> {
        // If it's already a Blob, we're done
        if (input instanceof Blob) {
            return { blob: input, mime: input.type || mime };
        }

        // It's a string: either a full data URL or raw base64
        const s = input.trim();

        if (s.startsWith('data:')) {
            // Already a data URL -> fetch it and return a Blob
            const res = await fetch(s);
            const blob = await res.blob();
            return { blob, mime: blob.type || mime };
        }

        // Assume raw base64 payload
        const res = await fetch(`data:${mime};base64,${s}`);
        const blob = await res.blob();
        return { blob, mime: blob.type || mime };
    }


}
